import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { 
  getFirestore, 
  initializeFirestore,
  memoryLocalCache,
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  getDocFromServer,
  setDoc, 
  addDoc, 
  updateDoc, 
  onSnapshot, 
  query, 
  where,
  deleteDoc,
  serverTimestamp,
  writeBatch,
  setLogLevel
} from 'firebase/firestore';
import firebaseConfigJson from '../../firebase-applet-config.json';
import { UserProfile, PackageItem, ShipmentOrder, UserRole, AppNotification, SupportTicket, PersonalShopperOrder, BlogPost, YahooAuctionBid } from '../types';
import { DEFAULT_STORE_PRODUCTS } from '../data/defaultProducts';
import { generateSlug } from '../utils/routes';

const firebaseConfig = {
  apiKey: firebaseConfigJson.apiKey,
  authDomain: firebaseConfigJson.authDomain,
  projectId: firebaseConfigJson.projectId,
  storageBucket: firebaseConfigJson.storageBucket,
  messagingSenderId: firebaseConfigJson.messagingSenderId,
  appId: firebaseConfigJson.appId,
};

// Mute all internal Firestore console noise (like idle stream timeouts and quota warnings)
// to prevent them from crashing or polluting the preview environment.
setLogLevel('silent');

export const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

const customDbId = (firebaseConfigJson as Record<string, string>).firestoreDatabaseId || '(default)';

// Initialize Firestore with in-memory local cache to prevent multi-tab primary lease lock contention
let dbInstance;
try {
  dbInstance = initializeFirestore(app, {
    localCache: memoryLocalCache(),
    experimentalAutoDetectLongPolling: true
  }, customDbId);
} catch (e) {
  dbInstance = getFirestore(app, customDbId);
}

export const db = dbInstance;

async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Please check your Firebase configuration.");
    }
  }
}
testConnection();

// Initialize Firebase Auth with browserLocalPersistence and inMemoryPersistence
// to avoid "Internal error opening backing store for indexedDB.open" in iframe/sandboxed environments
let authInstance;
try {
  authInstance = initializeAuth(app, {
    persistence: [browserLocalPersistence, browserSessionPersistence, inMemoryPersistence]
  });
} catch (e) {
  authInstance = getAuth(app);
}

export const auth = authInstance;

// Helper constants for Japan Warehouse Address
export const JAPAN_WAREHOUSE_BASE = {
  postalCode: '350-1151',
  prefecture: 'Saitama-ken (埼玉県)',
  city: 'Kawagoe-shi (川越市)',
  addressLine1: 'Imafuku 766-9',
  phone: '080-4859-1502',
  country: 'Japan (日本)',
  recipientBase: 'Do Japao'
};

// Master Admin User Definition (always protected)
export const MASTER_ADMIN_USER: UserProfile = {
  uid: 'admin-egami-001',
  email: 'egami.corporation@gmail.com',
  password: 'Reiko.8879',
  role: 'admin',
  suiteNumber: 'DJ-ADMIN',
  name: 'Depósito Do Japão (Admin)',
  phone: '080-4859-1502',
  emailVerified: true,
  createdAt: new Date().toISOString()
};

// Production Default Admin User (Client suites, stock packages, and shipments start completely empty)
export const INITIAL_DEMO_USERS: UserProfile[] = [
  MASTER_ADMIN_USER
];

export const INITIAL_DEMO_PACKAGES: PackageItem[] = [];

export const INITIAL_DEMO_SHIPMENTS: ShipmentOrder[] = [];
export const INITIAL_DEMO_NOTIFICATIONS: AppNotification[] = [];
export const INITIAL_DEMO_TICKETS: SupportTicket[] = [];
export const INITIAL_DEMO_PERSONAL_SHOPPER: PersonalShopperOrder[] = [];
export const INITIAL_DEMO_YAHOO_AUCTION_BIDS: YahooAuctionBid[] = [];

const RAW_INITIAL_DEMO_BLOG_POSTS: BlogPost[] = [
  {
    id: 'post-1',
    title: 'Como Comprar no Mercari Japan e Enviar para o Brasil',
    slug: 'como-comprar-no-mercari-japan',
    excerpt: 'Descubra como encontrar figures raros, roupas de grife e colecionáveis no maior brechó online do Japão com a nossa Suíte.',
    content: `O **Mercari Japan** (メルカリ) é o maior marketplace P2P (pessoa para pessoa) do Japão. Lá você encontra desde figures raros de anime e mangás antigos até roupas vintage, peças de videogame clássico e gadgets exclusivos por preços inacreditáveis!

### Por que os produtos do Mercari são tão baratos?
Os japoneses são extremamente cuidadosos com a conservação de seus objetos. Um item marcado como "usado" (中古) no Mercari frequentemente chega parecendo novinho em folha, com embalagem original e manuais intactos!

### Passo a passo para comprar no Mercari com a Do Japão:
1. **Acesse o site ou app do Mercari JP**: Navegue usando a tradução do navegador em português ou inglês.
2. **Copie o Link do Produto**: Encontrou o que procurava? Copie a URL da página do item.
3. **Solicite a Compra pelo Redirecionamento ou Comprador Pessoal**:
   - Se o vendedor aceitar envio para o nosso endereço de Saitama, você pode pagar direto no cartão de crédito/paypal e colocar o endereço da sua **Suíte Do Japão**.
   - Se precisar de intermediação (vendedor exige pagamento local ou cartão japonês), utilize nosso serviço de **Comprador Pessoal (Personal Shopper)** no painel!
4. **Receba no Nosso Depósito**: Assim que o pacote chegar ao nosso depósito, nós tiramos fotos e cadastramos o peso na sua Suíte para você solicitar a consolidação.

### Dica de Ouro Do Japão 💡
No Mercari, preste atenção na reputação do vendedor (estrelas) e se o valor já inclui o frete interno no Japão (**送料込み** - Shipping Included). Se tiver **着払い** (Pay on Delivery), a taxa de entrega local será cobrada na chegada do depósito.`,
    coverImage: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1000&auto=format&fit=crop&q=80',
    category: 'Guias de Compra',
    tags: ['Mercari', 'Colecionáveis', 'Anime', 'Economia', 'Guia'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 5,
    published: true,
    featured: true,
    views: 1240,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'post-2',
    title: 'Guia Completo: EMS vs Small Packet Airmail vs Frete Navio (Surface)',
    slug: 'guia-comparativo-fretes-japao-brasil',
    excerpt: 'Qual opção de frete vale mais a pena? Analisamos prazos, limites de peso e custos para você economizar ao máximo.',
    content: `Escolher a modalidade correta de envio do Japão para o Brasil pode salvar centenas de reais no custo total do seu pedido. A **Do Japão** oferece os principais métodos oficiais do **Japan Post** e transportadoras internacionais.

### 1. Japan Post - EMS (Express Mail Service)
- **Velocidade**: Média de 7 a 15 dias úteis para o Brasil.
- **Rastreio**: Completo ponto a ponto (de Tóquio até sua porta via Correios do Brasil).
- **Ideal para**: Pacotes valiosos, eletrônicos, figures delicados ou quando você tem pressa.
- **Limite de Peso**: Até 30 kg por caixa.

### 2. Small Packet (Airmail registrado)
- **Velocidade**: Média de 12 a 25 dias úteis.
- **Preço**: Consideravelmente mais barato que o EMS para itens leves.
- **Limite de Peso**: Máximo de 2,0 kg por pacote.
- **Ideal para**: Roupas, cosméticos, snacks japoneses, pequenos jogos e chaveiros.

### 3. Frete Marítimo / Navio (Surface Mail)
- **Velocidade**: 60 a 90 dias úteis.
- **Preço**: O menor custo por quilo do mercado.
- **Ideal para**: Lotes volumosos, caixas pesadas (acima de 10kg), livros, autopeças ou coleções completas sem urgência.

### Nosso Serviço de Consolidação & Redução de Caixa
Na sua Suíte Do Japão, você pode juntar pacotes de várias lojas diferentes (Amazon JP, Rakuten, Surugaya) em uma única caixa otimizada. Nossa equipe remove caixas externas desnecessárias para garantir que seu pacote fique no menor peso e tamanho possível!`,
    coverImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1000&auto=format&fit=crop&q=80',
    category: 'Importação e Logística',
    tags: ['Frete', 'EMS', 'Japan Post', 'Correios', 'Economia'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 7,
    published: true,
    featured: true,
    views: 980,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'post-3',
    title: 'Como Arrematar no Yahoo! Auctions Japan Sem Passar Raiva',
    slug: 'como-arrematar-no-yahoo-auctions-japan',
    excerpt: 'Aprenda os segredos dos leilões japoneses: como dar lances no último minuto e arrematar preciosidades relíquia.',
    content: `O **Yahoo! Auctions Japan** (ヤフオク!) é o maior portal de leilões do Japão. É lá que colecionadores do mundo inteiro procuram relíquias como relógios Seiko/G-Shock raros, guitarras Fender Japan, cards de Pokémon primeira edição e games retro.

### Dicas para arrematar com sucesso:
1. **Verifique a Reputação do Vendedor**: Dê preferência a vendedores com taxa de aprovação superior a 98% e mais de 100 avaliações positivas (良い).
2. **Atenção ao Horário de Encerramento**: Os leilões no Japão encerram no horário local de Tóquio (JST - UTC+9). Verifique a conversão para o fuso do Brasil!
3. **Extensão Automática (Automatic Extension)**: A maioria dos leilões do Yahoo! estende o prazo por +5 minutos se houver um lance nos minutos finais.
4. **Use nosso Serviço de Comprador Pessoal**: Se você não possui cartão de crédito do Japão para dar lances diretamente no Yahoo! Auctions, nossa equipe faz o lance por você com o valor máximo desejado!`,
    coverImage: 'https://images.unsplash.com/photo-1579226905180-636b76d96082?w=1000&auto=format&fit=crop&q=80',
    category: 'Promoções Imperdíveis',
    tags: ['Yahoo Auctions', 'Leilão', 'Relíquias', 'Retro Games'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 4,
    published: true,
    featured: false,
    views: 650,
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'post-4',
    title: 'Top 10 Produtos em Alta no Japão Mais Procurados para Importar em 2026',
    slug: 'top-10-produtos-em-alta-no-japao-2026',
    excerpt: 'De action figures exclusivas a eletrônicos retro e cosméticos virais: confira o que está bombando no e-commerce japonês.',
    content: `O mercado consumidor japonês lança constantemente itens exclusivos, colecionáveis de edição limitada e inovações que viram febre no mundo inteiro. Se você quer estar à frente das tendências e importar o que há de melhor, preparamos o ranking definitivo dos **Produtos em Alta no Japão** neste momento!

### 1. Action Figures e Estátuas de Edição Limitada (Bandai / Good Smile)
Action figures das franquias *Dragon Ball*, *One Piece*, *Demon Slayer* e *Jujutsu Kaisen* lançados exclusivamente via Ichiban Kuji (一番くじ) ou Premium Bandai continuam sendo os campeões de buscas no Mercari JP.

### 2. Consoles e Games Retro Conservados (Game Boy, PS2, Famicom)
Lojas de Akihabara e leilões do Yahoo! Auctions oferecem videogames antigos em excelente estado de conservação, com caixa original e manuais sem rasuras por frações do preço cobrado no Brasil.

### 3. Câmeras e Lentes Fotográficas Vintage (Canon, Nikon, Fujifilm)
Câmeras digitais de bolso dos anos 2000 (Digicams Y2K) e lentes analógicas japonesas estão super em alta entre fotógrafos e criadores de conteúdo.

### 4. Roupas e Streetwear Japonês (BAPE, Evisu, Comme des Garçons)
Roupas de grife e peças vintage de streetwear japonês encontradas no Mercari JP são negociadas por ótimos preços devido ao câmbio favorável do Iene.

### 5. Snacking & Utensílios de Cozinha Japoneses
Doces temáticos sazonais (KitKat sabor Matcha, Sakura, Batata Doce) e facas de chef artesanais de Seki e Sakai.

---

### Como Comprar Produtos em Alta com a Do Japão:
Basta copiar o link do produto no Mercari, Yahoo! ou Amazon JP e solicitar pelo nosso serviço de **Comprador Pessoal** ou usar o endereço da sua **Suíte Exclusiva** em Saitama!`,
    coverImage: 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?w=1000&auto=format&fit=crop&q=80',
    category: 'Novidades e Produtos em Alta',
    tags: ['Produtos em Alta', 'Trends', 'Japan', 'Mercari JP', 'Colecionáveis', '2026'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 6,
    published: true,
    featured: true,
    views: 1890,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-5',
    title: 'Como Importar do Japão e Economizar até 80%',
    slug: 'como-importar-do-japao-e-economizar-ate-80',
    excerpt: 'Descubra o passo a passo completo para comprar direto de lojas japonesas, leilões do Yahoo e Mercari economizando até 80% em relação aos preços do Brasil.',
    content: `Importar diretamente do Japão é o segredo de lojistas, colecionadores e entusiastas que conseguem figures raros, cosméticos premium (J-Beauty), eletrônicos vintage e roupas de grife por uma fração do preço cobrado no Brasil.

### Por que a economia chega a até 80%?
1. **Desvalorização Histórica do Iene (JPY)**: O iene japonês está em patamares cambiais extremamente favoráveis frente ao Real e ao Dólar.
2. **Mercado de Segunda Mão Impecável**: No Japão, itens classificados como usados frequentemente estão em estado de novos, lacrados ou com pouquíssimo uso.
3. **Eliminação de Intermediários**: Ao comprar na Amazon JP, Mercari, Surugaya ou Yahoo Auctions com endereço local (Sua Suíte Do Japão), você paga o preço do mercado doméstico japonês.

### Passo a passo para importar economizando ao máximo:
1. **Cadastre-se na Do Japão**: Obtenha seu endereço físico e número de Suíte exclusivo em Saitama (região metropolitana de Tóquio).
2. **Faça suas compras**: Use a sua Suíte como endereço de entrega nas lojas online japonesas ou use nosso serviço de Comprador Pessoal (Personal Shopper).
3. **Consolidação Inteligente**: Junte compras de diferentes lojas em uma única caixa otimizada. Nossa equipe descarta embalagens volumosas para reduzir o peso cubado e o valor do frete internacional.
4. **Envio Rápido e Seguro**: Escolha entre Japan Post EMS (rápido e rastreado), Small Packet Airmail (econômico) ou frete marítimo para cargas pesadas.`,
    coverImage: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1000&auto=format&fit=crop&q=80',
    category: 'Guias de Compra',
    tags: ['Economia', 'Importação', 'Japão', 'Mercari', 'Dicas'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 5,
    published: true,
    featured: true,
    views: 3120,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-6',
    title: 'Como Calcular a Margem de Lucro de um Produto Importado',
    slug: 'como-calcular-a-margem-de-lucro-de-um-produto-importado',
    excerpt: 'Aprenda a precificar produtos importados do Japão para revenda com alta rentabilidade considerando frete, Iene e impostos.',
    content: `Para quem revende colecionáveis, action figures, games retro, peças automotivas ou cosméticos importados do Japão, o cálculo preciso dos custos é a chave para garantir uma margem de lucro sólida e previsível.

### A Fórmula do Custo Real Unitário:
Custo Total = (Preço de Compra em JPY × Cotação) + Frete Doméstico JP + Taxa de Consolidação + Frete Internacional por Quilo + Impostos Aduaneiros Estimados.

### Margem de Lucro Bruta vs Líquida:
- **Margem Bruta**: Preço de Venda Final - Custo da Mercadoria com Frete.
- **Margem Líquida**: Margem Bruta deduzidas taxas de marketplace (Mercado Livre, Shopee), embalagem e impostos locais da empresa.

### Dica para Aumentar sua Margem:
Sempre consolide vários itens na mesma caixa. O custo do frete internacional do primeiro quilo é proporcionalmente o mais alto; a partir do segundo quilo, o custo por item despencará, elevando sua margem líquida para mais de 50%!`,
    coverImage: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1000&auto=format&fit=crop&q=80',
    category: 'Negócios e Revenda',
    tags: ['Margem de Lucro', 'Revenda', 'Empreendedorismo', 'Finanças'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 6,
    published: true,
    featured: false,
    views: 1450,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-7',
    title: 'Transferência e Pagamentos Internacionais via Western Union e Câmbio',
    slug: 'transferencia-via-western-union',
    excerpt: 'Como realizar pagamentos e transferências internacionais seguras para sua conta ou compras no Japão via Western Union, PIX e Wise.',
    content: `Ao comprar no Japão, ter métodos de pagamento ágeis, transparentes e com IOF reduzido faz toda a diferença no custo final.

### Métodos aceitos e recomendados pela Do Japão:
1. **PIX Instantâneo (Brasil)**: Pagamento em reais convertido em tempo real com confirmação imediata no sistema.
2. **Cartão de Crédito Nacional e Internacional**: Parcelamento e aprovação em segundos.
3. **Western Union & Wise**: Para remessas diretas de câmbio de alta quantia ou pagamentos de leilões e contas no Japão com tarifas mínimas.
4. **PayPal & Carteira Digital**: Proteção ao comprador global.

Em caso de dúvidas sobre transferências internacionais ou envio via Western Union, entre em contato direto pelo nosso WhatsApp oficial de atendimento!`,
    coverImage: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1000&auto=format&fit=crop&q=80',
    category: 'Pagamentos',
    tags: ['Western Union', 'Câmbio', 'PIX', 'Pagamentos Internacionais'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 4,
    published: true,
    featured: false,
    views: 890,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-8',
    title: 'Agende sua Consultoria de Importação do Japão',
    slug: 'agende-sua-consultoria',
    excerpt: 'Mentoria individual para colecionadores, lojistas e revendedores que desejam importar produtos exclusivos do Japão com assessoria especializada.',
    content: `Quer começar a importar profissionalmente ou encontrar fornecedores exclusivos em Tóquio, Osaka e Akihabara?

Nossa consultoria especializada oferece:
- Mapeamento das melhores lojas japonesas para o seu nicho.
- Estratégias avançadas de arremate no Yahoo! Auctions Japan.
- Dicas para encontrar raridades no Mercari Japan antes de outros compradores.
- Otimização aduaneira e redução de custos operacionais.
- Suporte dedicado com nosso time bilíngue residente no Japão há mais de 10 anos.

Fale com nossa equipe pelo suporte online ou agende sua sessão pelo WhatsApp oficial da Do Japão!`,
    coverImage: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1000&auto=format&fit=crop&q=80',
    category: 'Consultoria',
    tags: ['Consultoria', 'Assessoria', 'Atacado', 'Mentoria'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 3,
    published: true,
    featured: false,
    views: 1120,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-9',
    title: 'Termos e Condições do Programa de Afiliados Do Japão',
    slug: 'affiliate-tos-page',
    excerpt: 'Regulamento oficial para parceiros, influenciadores e afiliados da plataforma Do Japão.',
    content: `### Termos e Condições Gerais do Programa de Parceiros Do Japão

1. **Elegibilidade**: O programa é aberto a criadores de conteúdo, canais do YouTube, páginas do Instagram, blogs e clientes que recomendam o serviço de redirecionamento.
2. **Comissões**: Os afiliados recebem percentuais sobre envios internacionais concluídos e serviços adicionais contratados por clientes indicados através do seu link exclusivo.
3. **Pagamentos**: Os saques das comissões são efetuados mensalmente via PIX ou crédito na carteira da plataforma para uso em fretes futuros.
4. **Conduta Ética**: É proibida a prática de spam ou divulgação enganosa de valores e prazos. As informações fornecidas devem seguir as diretrizes oficiais de Do Japão.`,
    coverImage: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=1000&auto=format&fit=crop&q=80',
    category: 'Políticas',
    tags: ['Afiliados', 'Termos', 'Regulamento', 'Parcerias'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 4,
    published: true,
    featured: false,
    views: 740,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-10',
    title: 'Programa de Afiliados Do Japão - Regulamento Completo',
    slug: 'affiliate-tos-page-2-2',
    excerpt: 'Políticas detalhadas de comissionamento, pagamentos e boas práticas para afiliados e divulgadores.',
    content: `### Regulamento de Afiliados - Aditivo e Normas Operacionais

Este documento complementa os termos gerais do Programa de Afiliados Do Japão.

- **Rastreamento de Cookies**: Os cookies de indicação possuem validade de 60 dias a contar do primeiro clique do usuário indicado.
- **Suporte aos Indicados**: A equipe de suporte da Do Japão se encarrega integralmente do atendimento e suporte logístico ao cliente cadastrado.
- **Relatórios**: Painel exclusivo com visualização em tempo real de cliques, cadastros de suítes ativas e envios consolidados.`,
    coverImage: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=1000&auto=format&fit=crop&q=80',
    category: 'Políticas',
    tags: ['Afiliados', 'Regulamento', 'Políticas'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 3,
    published: true,
    featured: false,
    views: 620,
    createdAt: new Date().toISOString()
  },
  {
    id: 'post-11',
    title: 'Guia Oficial de Importação e Redirecionamento 2026',
    slug: '37749-2',
    excerpt: 'Tudo o que você precisa saber sobre compras no Japão, armazenamento gratuito no depósito de Saitama e envio seguro para o Brasil.',
    content: `A **Do Japão** é sua parceira de confiança para compras no mercado japonês.

Com nosso armazém logístico estrategicamente posicionado em Saitama (Tóquio metropolitana), você tem:
- **30 dias de armazenamento gratuito** para acumular compras de várias lojas.
- **Fotos em alta resolução** de cada produto recebido.
- **Consolidação profissional** com embalagens reforçadas, plástico-bolha e redução de espaço.
- **Suporte 100% em Português** para esclarecer qualquer dúvida de importação e rastreio.`,
    coverImage: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1000&auto=format&fit=crop&q=80',
    category: 'Guias',
    tags: ['Guia', 'Redirecionamento', 'Depósito Japão'],
    author: 'Equipe Do Japão',
    readTimeMinutes: 4,
    published: true,
    featured: false,
    views: 1040,
    createdAt: new Date().toISOString()
  }
];

export const INITIAL_DEMO_BLOG_POSTS: BlogPost[] = RAW_INITIAL_DEMO_BLOG_POSTS.map(post => ({
  ...post,
  slug: generateSlug(post.title)
}));

// Global flag to track if Firestore free-tier quota has been reached
export let isFirestoreQuotaExhausted = (() => {
  try {
    const stored = localStorage.getItem('dojapao_firestore_quota_exhausted');
    if (stored) {
      const { timestamp } = JSON.parse(stored);
      // Reset check after 4 hours
      if (Date.now() - timestamp < 4 * 60 * 60 * 1000) {
        return true;
      }
    }
  } catch (e) {}
  return false;
})();

export function markFirestoreQuotaExhausted(reason?: any) {
  if (!isFirestoreQuotaExhausted) {
    isFirestoreQuotaExhausted = true;
    try {
      localStorage.setItem(
        'dojapao_firestore_quota_exhausted',
        JSON.stringify({ timestamp: Date.now() })
      );
    } catch (e) {}
    console.warn('⚠️ [Firestore Quota Protection] Limite de escrita diária do Firestore atingido. O sistema ativou o modo de alta disponibilidade via Cache Local e Servidor.');
  }
}

export function isQuotaExhaustedError(err: any): boolean {
  if (!err) return false;
  const msg = typeof err === 'string' ? err : (err.message || err.code || '');
  return (
    err.code === 'resource-exhausted' ||
    msg.includes('Quota limit exceeded') ||
    msg.includes('Quota exceeded') ||
    msg.includes('RESOURCE_EXHAUSTED') ||
    msg.includes('free tier database') ||
    msg.includes('resource-exhausted')
  );
}

let isSeedingProcessRunning = false;

// Always ensure the master admin user is present and purge any duplicates
export async function ensureAdminUserExists(): Promise<UserProfile> {
  if (isFirestoreQuotaExhausted) {
    return MASTER_ADMIN_USER;
  }

  try {
    const adminRef = doc(db, 'users', MASTER_ADMIN_USER.uid);
    const snap = await getDoc(adminRef);
    if (!snap.exists()) {
      try {
        await setDoc(adminRef, MASTER_ADMIN_USER);
        console.log('✅ Conta de administrador restaurada com sucesso no Firestore.');
      } catch (writeErr: any) {
        if (isQuotaExhaustedError(writeErr)) {
          markFirestoreQuotaExhausted(writeErr);
        }
      }
    } else {
      // Ensure role is admin and secure password
      const currentData = snap.data();
      if (
        currentData.role !== 'admin' ||
        currentData.suiteNumber !== 'DJ-ADMIN' ||
        !currentData.password ||
        currentData.password === 'teste12345' ||
        currentData.password === 'admin123'
      ) {
        try {
          await setDoc(
            adminRef,
            {
              ...currentData,
              role: 'admin',
              suiteNumber: 'DJ-ADMIN',
              password: MASTER_ADMIN_USER.password
            },
            { merge: true }
          );
        } catch (mergeErr: any) {
          if (isQuotaExhaustedError(mergeErr)) {
            markFirestoreQuotaExhausted(mergeErr);
          }
        }
      }
    }
  } catch (err: any) {
    if (isQuotaExhaustedError(err)) {
      markFirestoreQuotaExhausted(err);
    } else {
      console.warn('Notice ensuring admin user in Firestore:', err);
    }
  }
  return MASTER_ADMIN_USER;
}

// Initialize Firestore Collections with default data if empty (using writeBatch for efficiency)
export async function seedInitialDataIfNeeded() {
  if (isFirestoreQuotaExhausted) return;

  // Always verify and restore master admin
  await ensureAdminUserExists();

  const isAlreadySeeded = localStorage.getItem('dojapao_db_seeded_flag');
  if (isAlreadySeeded === 'true' || isSeedingProcessRunning || isFirestoreQuotaExhausted) return;

  isSeedingProcessRunning = true;
  try {
    const batch = writeBatch(db);
    let hasNewData = false;

    // Check and seed missing blog posts only
    for (const post of INITIAL_DEMO_BLOG_POSTS) {
      const bSnap = await getDoc(doc(db, 'blog_posts', post.id));
      if (!bSnap.exists()) {
        batch.set(doc(db, 'blog_posts', post.id), post);
        hasNewData = true;
      }
    }

    if (hasNewData && !isFirestoreQuotaExhausted) {
      await batch.commit();
    }
    localStorage.setItem('dojapao_db_seeded_flag', 'true');
  } catch (err: any) {
    if (isQuotaExhaustedError(err)) {
      markFirestoreQuotaExhausted(err);
    } else {
      console.warn('Firestore seed check notice (using local state):', err?.message || err);
    }
  } finally {
    isSeedingProcessRunning = false;
  }
}
